/**
 * Skeleton Loader Utility
 * Provides skeleton loading states for the entire site
 */

class SkeletonLoader {
  constructor() {
    this.init();
  }

  init() {
    // Show skeleton on page load
    this.showPageSkeleton();
    
    // Hide skeleton when page is fully loaded
    if (document.readyState === 'complete') {
      this.hidePageSkeleton();
    } else {
      window.addEventListener('load', () => this.hidePageSkeleton());
    }

    // Handle AJAX navigation
    this.handleAjaxNavigation();
  }

  showPageSkeleton() {
    const body = document.body;
    if (!body.classList.contains('skeleton-loading')) {
      body.classList.add('skeleton-loading');
    }
  }

  hidePageSkeleton() {
    const body = document.body;
    const skeletons = document.querySelectorAll('.skeleton');
    
    // Fade out skeletons
    skeletons.forEach((skeleton) => {
      skeleton.classList.add('skeleton-fade-out');
    });

    // Remove skeleton class after animation
    setTimeout(() => {
      body.classList.remove('skeleton-loading');
      skeletons.forEach((skeleton) => {
        skeleton.classList.add('skeleton-hidden');
      });
    }, 300);
  }

  showSkeleton(container) {
    if (!container) return;
    
    const skeletons = container.querySelectorAll('.skeleton-hidden');
    skeletons.forEach((skeleton) => {
      skeleton.classList.remove('skeleton-hidden', 'skeleton-fade-out');
    });
  }

  hideSkeleton(container) {
    if (!container) return;
    
    const skeletons = container.querySelectorAll('.skeleton:not(.skeleton-hidden)');
    skeletons.forEach((skeleton) => {
      skeleton.classList.add('skeleton-fade-out');
      setTimeout(() => {
        skeleton.classList.add('skeleton-hidden');
      }, 300);
    });
  }

  showProductGridSkeleton(container, count = 4) {
    if (!container) return;
    
    const grid = container.querySelector('.product-grid, .grid');
    if (!grid) return;

    const skeletonHTML = Array(count)
      .fill(0)
      .map(() => `
        <div class="grid__item">
          <div class="skeleton-product-card">
            <div class="skeleton skeleton-product-card__image"></div>
            <div class="skeleton-product-card__content">
              <div class="skeleton skeleton-text skeleton-product-card__title"></div>
              <div class="skeleton skeleton-text skeleton-product-card__price"></div>
            </div>
          </div>
        </div>
      `)
      .join('');

    grid.innerHTML = skeletonHTML;
  }

  showCartSkeleton(container) {
    if (!container) return;
    
    const cartItems = container.querySelector('.cart-items tbody, .cart__items');
    if (!cartItems) return;

    const skeletonHTML = Array(3)
      .fill(0)
      .map(() => `
        <tr class="skeleton-cart-item">
          <td class="cart-item__media">
            <div class="skeleton skeleton-cart-item__image" style="width: 12rem; height: 12rem; border-radius: 16px;"></div>
          </td>
          <td class="cart-item__details">
            <div class="skeleton skeleton-text" style="height: 2rem; width: 60%; margin-bottom: 1rem;"></div>
            <div class="skeleton skeleton-text" style="height: 1.8rem; width: 30%;"></div>
          </td>
          <td class="cart-item__quantity">
            <div class="skeleton" style="width: 10rem; height: 4.5rem; border-radius: 8px;"></div>
          </td>
          <td class="cart-item__totals">
            <div class="skeleton skeleton-text" style="height: 2rem; width: 8rem; margin-left: auto;"></div>
          </td>
        </tr>
      `)
      .join('');

    if (cartItems.tagName === 'TBODY') {
      cartItems.innerHTML = skeletonHTML;
    } else {
      cartItems.innerHTML = skeletonHTML;
    }
  }

  handleAjaxNavigation() {
    // Listen for cart updates
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        const cartContainer = document.querySelector('cart-items, #main-cart-items');
        if (cartContainer) {
          const items = cartContainer.querySelectorAll('.cart-item');
          if (items.length === 0) {
            this.showCartSkeleton(cartContainer);
          }
        }
      });
    }

    // Listen for collection/product grid updates
    document.addEventListener('collection:update', (event) => {
      const container = event.detail?.container;
      if (container) {
        this.showProductGridSkeleton(container);
      }
    });

    // Show skeleton on navigation
    document.addEventListener('shopify:section:load', () => {
      this.showPageSkeleton();
      setTimeout(() => this.hidePageSkeleton(), 500);
    });
  }

  // Show skeleton for specific sections
  showSectionSkeleton(sectionSelector) {
    const section = document.querySelector(sectionSelector);
    if (!section) return;

    const skeletons = section.querySelectorAll('.skeleton-hidden');
    skeletons.forEach((skeleton) => {
      skeleton.classList.remove('skeleton-hidden', 'skeleton-fade-out');
    });
  }

  // Hide skeleton for specific sections
  hideSectionSkeleton(sectionSelector) {
    const section = document.querySelector(sectionSelector);
    if (!section) return;

    const skeletons = section.querySelectorAll('.skeleton:not(.skeleton-hidden)');
    skeletons.forEach((skeleton) => {
      skeleton.classList.add('skeleton-fade-out');
      setTimeout(() => {
        skeleton.classList.add('skeleton-hidden');
      }, 300);
    });
  }
}

// Initialize skeleton loader
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.skeletonLoader = new SkeletonLoader();
  });
} else {
  window.skeletonLoader = new SkeletonLoader();
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SkeletonLoader;
}
